import React from 'react'

import { Box, styled } from '@mui/material'

export type HardcoverOrientation = 'square' | 'portrait' | 'landscape'

interface HardcoverMockupProps {
  /** Personalised cover image URL to overlay onto the blank book mockup. */
  coverImageUrl: string | null
  /** Book orientation; usually derived from the selected print preset. */
  orientation: HardcoverOrientation
  /** Optional alt text for accessibility (defaults to a generic label). */
  alt?: string
  /** Optional max width in CSS (e.g. '420px'); defaults to '100%'. */
  maxWidth?: string
}

// Cover-area bounds within each mockup PNG, expressed as percentages of the
// mockup image dimensions. We push the rect ~1.5% outward from the detected
// white area on each side so the cover image extends through the case-wrap
// edge of the book rather than leaving a visible unprinted margin. If the
// mockups are regenerated these must be re-measured; see
// books-creator/story_generation/tools/generate-image.py for the generator.
const COVER_RECTS: Record<HardcoverOrientation, {
  top: string
  left: string
  width: string
  height: string
}> = {
  square: { top: '11.2%', left: '13%', width: '74.8%', height: '73.6%' },
  portrait: { top: '8.3%', left: '13.3%', width: '74.4%', height: '79.9%' },
  landscape: { top: '16.8%', left: '18.2%', width: '64%', height: '62.9%' },
}

const MOCKUP_PATHS: Record<HardcoverOrientation, string> = {
  square: '/images/hardcover-mockups/hardcover-square.webp',
  portrait: '/images/hardcover-mockups/hardcover-portrait.webp',
  landscape: '/images/hardcover-mockups/hardcover-landscape.webp',
}

const FRAME_ASPECT: Record<HardcoverOrientation, number> = {
  square: 1,
  portrait: 896 / 1200,
  landscape: 1200 / 896,
}

// Subtle rounding so the cover's corners track the case wrap radius instead
// of poking out as sharp 90-degree angles. Fixed pixels (not %) keeps the
// curve consistent across render sizes.
const COVER_BORDER_RADIUS = '4px'

/**
 * Pick an orientation bucket from the book's width-to-height ratio. The
 * personalize wizard already computes `w / h` for the selected print preset,
 * so callers can pass that directly.
 */
export function orientationFromAspectRatio(ratio: number): HardcoverOrientation {
  if (ratio > 1.1) return 'landscape'
  if (ratio < 0.9) return 'portrait'

  return 'square'
}

export const HardcoverMockup: React.FC<HardcoverMockupProps> = ({
  coverImageUrl,
  orientation,
  alt = 'Personalised hardcover storybook preview',
  maxWidth = '100%',
}) => {
  const rect = COVER_RECTS[orientation]
  const mockup = MOCKUP_PATHS[orientation]
  const aspect = FRAME_ASPECT[orientation]

  return (
    <StyledFrame style={{ aspectRatio: aspect, maxWidth }}>
      <StyledMockupImage src={mockup} alt="" aria-hidden />
      {coverImageUrl && (
        <StyledCoverSlot
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: COVER_BORDER_RADIUS,
          }}
        >
          <StyledCoverImage src={coverImageUrl} alt={alt} />
          {/* Lighting overlay: faint top-left highlight + slight bottom-right
              vignette + edge falloff. Sells the "printed on the cover" look
              without new assets. */}
          <StyledLightingOverlay
            aria-hidden
            style={{ borderRadius: COVER_BORDER_RADIUS }}
          />
          {/* Soft shadow band along the spine edge so the binding fold
              reads as a curved transition rather than a hard line. */}
          <StyledSpineShadow aria-hidden />
          {/* Inner edge ring: where the printed cover meets the case wrap. */}
          <StyledInnerEdge
            aria-hidden
            style={{ borderRadius: COVER_BORDER_RADIUS }}
          />
        </StyledCoverSlot>
      )}
    </StyledFrame>
  )
}

const StyledFrame = styled(Box)({
  position: 'relative',
  width: '100%',
  margin: '0 auto',
})

const StyledMockupImage = styled('img')({
  width: '100%',
  height: '100%',
  display: 'block',
  userSelect: 'none',
  pointerEvents: 'none',
})

const StyledCoverSlot = styled(Box)({
  position: 'absolute',
  overflow: 'hidden',
})

const StyledCoverImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  userSelect: 'none',
})

const StyledLightingOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  // Two stacked gradients sell a single light source coming in from the
  // upper-left:
  //   1. Radial specular hot-spot: brightest right where the imaginary
  //      light hits, falling off quickly so the cover feels lit, not
  //      washed out.
  //   2. Linear directional sheen: matte-laminate lift on the lit side,
  //      gentle shadow rolling toward the lower-right.
  // `overlay` blend mode means the cover keeps its own colours — bright
  // areas brighten, dark areas deepen, mid-tones stay close to true.
  background: `
    radial-gradient(
      circle at 18% 12%,
      rgba(255, 255, 255, 0.38) 0%,
      rgba(255, 255, 255, 0.14) 22%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.16) 0%,
      rgba(255, 255, 255, 0) 38%,
      rgba(0, 0, 0, 0) 58%,
      rgba(0, 0, 0, 0.18) 100%
    )
  `,
  mixBlendMode: 'overlay',
})

const StyledInnerEdge = styled(Box)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  // Subtle inner vignette + a 1px darker line where the printed cover sits
  // against the case wrap.
  boxShadow: `
    inset 0 0 0 1px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.10),
    inset 0 0 18px rgba(0, 0, 0, 0.10)
  `,
})

const StyledSpineShadow = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  width: '8%',
  pointerEvents: 'none',
  // Vertical shadow along the spine binding so the left edge reads as a
  // gentle curve rather than a flat seam. The gradient starts darker at
  // the very edge and fades inward.
  background: `linear-gradient(
    to right,
    rgba(0, 0, 0, 0.18) 0%,
    rgba(0, 0, 0, 0.06) 35%,
    rgba(0, 0, 0, 0) 100%
  )`,
})
